import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import ViewCustomerModal from "shared/modal/viewCustomerModal";
import {
  DeleteSupportMessageService,
  GetAllSupportMessagesService,
} from "shared/services/support";
import styles from "./style.module.scss";
import moment from "moment";

interface MessagesData {
  id: number;
  fullname: string;
  email: string;
  message: string;
  created_at: string;
  updated_at: string;
}

const SupportMessages = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [viewModal, setViewModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: string | null;
    message: MessagesData | null;
  }>({
    open: false,
    action: null,
    message: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [messages, setMessages] = useState<MessagesData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedMessage, setSelectedMessage] = useState<MessagesData | null>(
    null
  );

  const openConfirmModalFromViewModal = () => {
    if (!selectedMessage) return;
    setConfirmModal({
      open: true,
      action: "Delete",
      message: selectedMessage,
    });
  };

  const openViewModal = (message: MessagesData) => {
    setSelectedMessage(message);
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewModal(false);
    setSelectedMessage(null);
  };

  const openConfirmModal = (action: string, message: MessagesData) => {
    setConfirmModal({ open: true, action, message });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, action: null, message: null });
  };

  const handleConfirmAction = async () => {
    const id = confirmModal.message?.id || selectedMessage?.id;
    if (!id) return;
    try {
      const response = await DeleteSupportMessageService(id);
      if (
        response?.status === 201 ||
        response?.status === 200 ||
        response?.status === 204
      ) {
        toastMessage("success", "Message deleted successfully");
        setMessages((prevMessages) =>
          prevMessages.filter((message) => message.id !== id)
        );
        setTotalItems(totalItems - 1);
      } else {
        toastMessage("error", "Failed to delete the message");
      }
    } catch (error) {
      console.error("Error deleting message:", error);
      toastMessage("error", "Error: Could not delete message");
    } finally {
      closeConfirmModal();
      closeViewModal();
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handleGetAllMessages(page);
  };

  const messagesTableHeads = [
    { title: "Customer Name", key: "messages" },
    { title: "Email", key: "email" },
    { title: "Message", key: "message" },
    { title: "Date & Time", key: "dateTime" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllMessages = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetAllSupportMessagesService(page, 10);
      const MessagesData = Array.isArray(response.data.data.data)
        ? response.data.data.data
        : [];

      const updatedMessages =
        page === 1 ? MessagesData : [...messages, ...MessagesData];

      setMessages(updatedMessages);
      setTotalItems(response.data.data.meta.totalItems || 0);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetAllMessages();
    // eslint-disable-next-line
  }, []);

  const truncateMessage = (message: string, wordLimit: number = 6) => {
    const words = message.split(" ");
    if (words.length > wordLimit) {
      return `${words.slice(0, wordLimit).join(" ")}...`;
    }
    return message;
  };

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={messagesTableHeads}
          title="Support Messages List"
          isEmpty={!messages.length}
          loading={loading}
        >
          {loading
            ? Array.from({ length: 10 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : messages.map((items) => {
                return (
                  <tr key={items.id}>
                    <td style={{ maxWidth: "200px" }}>
                      <label className={classNames(styles.contentLabel)}>
                        {items.fullname}
                      </label>
                    </td>
                    <td>{items.email}</td>
                    <td className="text-justify">
                      {truncateMessage(items.message, 8)}
                    </td>{" "}
                    <td>{moment(items.created_at).format("MMM DD, YYYY")}</td>
                    <td className={classNames(styles.IconsContainer)}>
                      <CustomToolTip label="Details">
                        <button onClick={() => openViewModal(items)}>
                          <Icons.Eye />
                        </button>
                      </CustomToolTip>
                      <CustomToolTip label="Delete">
                        <button
                          onClick={() => openConfirmModal("Delete", items)}
                        >
                          <Icons.TrashOutline />
                        </button>
                      </CustomToolTip>
                    </td>
                  </tr>
                );
              })}
        </CustomTable>
      </div>

      <div className={classNames(styles.paginationContainer, "mt-3")}>
        <CustomPagination
          onPageChange={handlePageChange}
          totalCount={totalItems}
          currentPage={currentPage}
          pageSize={10}
        />
      </div>

      {selectedMessage && (
        <ViewCustomerModal
          isMessagesModal
          title="Message Details"
          show={viewModal}
          actionButtonText="Delete"
          handleClose={closeViewModal}
          listingDetails={{
            name: selectedMessage.fullname,
            phone: selectedMessage.email,
            address: selectedMessage.message,
            city: selectedMessage.created_at,
            profileImage: "",
          }}
          // @ts-ignore
          handleSubmit={openConfirmModalFromViewModal}
        />
      )}

      {confirmModal.open && (
        <ConfirmationModal
          show={confirmModal.open}
          title={`Confirm ${confirmModal.action}`}
          actionButtonText={`Yes, ${confirmModal.action}`}
          subtitle={`Are you sure you want to ${confirmModal.action?.toLowerCase()} this message?`}
          handleSubmit={handleConfirmAction}
          handleClose={closeConfirmModal}
        />
      )}
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 5 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default SupportMessages;
